import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LoginService } from '@app/common/login.service';
import { Attach, Event } from '@app/shared/_models/common';
import { Sales_order } from '@app/shared/_models/sales';
import { DocType, EventType, ObjectType, jsonheader } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';
import { pickedDateStr } from '@app/shared/time';
import { DeliveryPlanning } from '@app/logistic/logistic.service';

// Drapeau posé sur la pièce jointe « BL papier signé revenu de tournée » (c_attach.data.flags).
export const ATTACH_FLAG_SCAN_BL = 'scan_bl';

export interface TourReturnDelivery extends DeliveryPlanning {
	datinvoicing?: string | null;		// non nul = BL facturé : ses quantités sont scellées dans la facture
	ral?: number;						// reste à livrer de SA commande (o=ral), annulations déduites
	lnk_attachs?: Attach[];
	id_planning?: number | null;		// BL de vente d'un passage de location : le BL du dossier qu'il accompagne (703 les livre ensemble)
	lnk_deliveryl?: { id_order_l: number; qty: number | string }[] | null;	// o=lines ; qté négative = reprise d'un matériel loué
	lnk_order: DeliveryPlanning['lnk_order'] & { state?: SalesOrderState; doc_type?: DocType };	// servis par GET /deliveries en texte
}

// BL qu'un 703 livrerait en plus : l'autre moitié d'un passage de location (BL du dossier ou BL de vente).
export interface PassageExtra {
	id: number;
	id_order: number;
	doc_type: string;
}

// Une quantité par ligne de commande, pour un BL (704) ou un bon d'annulation (705).
export interface DeliveryLineQty {
	id_order_l: number;
	qty: number;
}

@Injectable({ providedIn: 'root' })
export class TourReturnService {

	private http = inject(HttpClient);
	private loginService = inject(LoginService);

	// Jour J : BL demandés J, réalisés J, et BL sans rendez-vous encore ouverts (filtre serveur tourday) ; sans limit le serveur coupe à 100.
	// lines : le signe des quantités dit livraison ou reprise d'un BL de location.
	getDeliveriesOfDay$(dat: Date): Observable<TourReturnDelivery[]> {
		const d = pickedDateStr(dat);
		return this.http.get<TourReturnDelivery[]>(environment.server_url + '/deliveries?q=tourday:' + d + ' AND limit:2000&o=attachs,ral,lines');
	}

	// Commande avec ses lignes et TOUS ses bons (livrés, à livrer, annulations) : de quoi calculer le reste à livrer.
	// Dossier de location (`bl`, #55) : lecture « pending » du serveur, lignes à livrer ou sur un BL ouvert et celles de ce BL, jamais tout le dossier.
	getOrderWithDeliveries$(id: number, bl?: number): Observable<Sales_order[]> {
		return this.http.get<Sales_order[]>(environment.server_url + '/sales/order/' + id + (bl ? '?o=deliveries,pending,bl:' + bl : '?o=deliveries'));
	}

	// Ce que le 703 de ces BL livrerait en plus (documentation.html#server.delivery.passage-location), lu avant la confirmation.
	getPassageExtra$(ids: number[]): Observable<PassageExtra[]> {
		return this.http.get<PassageExtra[]>(environment.server_url + '/deliveries/passages?ids=' + ids.join(','));
	}

	// Event 703 : realdatdel posé en une transaction ; réponse data = { done: id[], skipped: id[] }.
	postDeliveryReturn$(ids: number[], dat: Date): Observable<any> {
		return this.postEvent$(EventType.DELIVERY_RETURN, 0, { ids, dat: pickedDateStr(dat) });
	}

	// Event 704 : TOUTES les lignes du BL, remplacées (qté 0 = ligne retirée) ; refus métier si le BL est livré ou facturé.
	postDeliveryLines$(deliveryId: number, lines: DeliveryLineQty[]): Observable<any> {
		return this.postEvent$(EventType.DELIVERY_LINES, deliveryId, { lines });
	}

	// Event 705 : bon d'annulation du reste à livrer ; réponse data = { delivery_id, id_order, nb_lines }.
	postCancelRemaining$(orderId: number, lines: DeliveryLineQty[]): Observable<any> {
		return this.postEvent$(EventType.DELIVERY_CANCEL_REMAINING, 0, { id_order: orderId, lines });
	}

	private postEvent$(typ: EventType, objectid: number, data: any): Observable<any> {
		const event: Event = {
			id: 0,
			typ,
			userid: this.loginService.loggeduser?.id,
			objectid,
			objecttyp: ObjectType.DELIVERY,
			data,
		};
		return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
	}
}
